import { useEffect, useState } from "react";
import { Plus, X, Navigation, Bell, Loader2 } from "lucide-react";
import { apiFetch } from "@/lib/api";
import { PageTitle, PageSubtitle, Loader, EmptyState } from "@/components/common";

export default function PreferencesPage() {
  const [routes, setRoutes] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    apiFetch("/auth/me").then((u) => setRoutes(u.preferred_routes ?? [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const add = () => {
    const v = input.trim();
    if (v && !routes.includes(v)) {
      setRoutes((old) => [...old, v]);
      setSaved(false);
    }
    setInput("");
  };
  const remove = (r) => {
    setRoutes((old) => old.filter((it) => it !== r));
    setSaved(false);
  };
  const save = async () => {
    setSaving(true);
    setError("");
    setSaved(false);
    try {
      await apiFetch("/profile", { method: "PATCH", body: JSON.stringify({ preferred_routes: routes }) });
      setSaved(true);
    } catch (e) {
      setError(e.message || "Riprova.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <PageTitle>Notifiche intelligenti</PageTitle>
      <PageSubtitle>
        Salva tratte o zone di tuo interesse. I viaggi compatibili appena pubblicati compariranno nella sezione "Per te" della Home.
      </PageSubtitle>

      <div>
        <label className="mb-1.5 block text-xs font-bold text-card-foreground">Tratta o zona preferita</label>
        <div className="flex gap-2.5">
          <input
            data-testid="preferences-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && add()}
            placeholder="Es. Latina → Capua oppure Campania"
            className="min-h-[52px] flex-1 rounded-2xl border border-border bg-card px-4 text-[15px] outline-none focus:border-primary"
          />
          <button data-testid="preferences-add-button" onClick={add} className="flex h-[52px] w-[52px] items-center justify-center rounded-2xl bg-primary text-primary-foreground">
            <Plus className="h-6 w-6" />
          </button>
        </div>
      </div>

      {loading ? (
        <Loader />
      ) : routes.length === 0 ? (
        <EmptyState icon={Bell} title="Nessuna tratta salvata" text="Aggiungi la prima tratta per ricevere i viaggi compatibili nella Home." />
      ) : (
        <div className="flex flex-wrap gap-2">
          {routes.map((r) => (
            <span key={r} data-testid={`preference-chip-${r}`} className="inline-flex items-center gap-2 rounded-full bg-secondary px-3 py-2 text-[13px] font-bold text-secondary-foreground">
              <Navigation className="h-4 w-4" /> {r}
              <button data-testid={`preference-remove-${r}`} onClick={() => remove(r)}>
                <X className="h-4 w-4" />
              </button>
            </span>
          ))}
        </div>
      )}

      {error ? <p data-testid="preferences-error" className="text-center text-sm text-destructive">{error}</p> : null}
      {saved ? <p data-testid="preferences-saved" className="text-center text-sm font-bold text-[hsl(var(--success))]">Preferenze salvate. I viaggi compatibili appariranno nella Home.</p> : null}

      <button data-testid="preferences-save-button" onClick={save} disabled={saving || loading} className="flex min-h-[54px] w-full items-center justify-center gap-2 rounded-2xl bg-primary text-sm font-extrabold text-primary-foreground disabled:opacity-60">
        {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : null} SALVA PREFERENZE
      </button>
    </div>
  );
}
